import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { throwError } from 'rxjs';

import { InbetriebnahmeService } from '../inbetriebnahme.service';
import { OfflineDbService } from './offline-db.service';
import { NetworkStatusService, TimeoutFehler } from './network-status.service';

import { istLokaleId } from '../../Models/messstelle.model';
import {
  SyncAenderung,
  SyncErgebnis,
  SyncKonfliktEintrag,
  SyncQueueEintrag,
  SyncStatus
} from '../../Models/offline.model';

/** Warum ein Abgleich angestoßen wurde – nur für die Nachvollziehbarkeit im Log. */
export type SyncAnlass =
  | 'start'
  | 'online'
  | 'sichtbar'
  | 'geoeffnet'
  | 'aenderung'
  | 'manuell';

/**
 * Bringt offline erfasste Änderungen zum Server.
 *
 * Der Ablauf ist bewusst schlicht: Warteschlange in Erfassungsreihenfolge
 * abschicken, jede Antwort einzeln auswerten, und was nicht angekommen ist,
 * bleibt liegen. Es gibt keinen Zustand, in dem eine Änderung aus der
 * Warteschlange verschwindet, ohne dass sie entweder beim Server ist oder in
 * der Konfliktliste steht.
 *
 * Kein Polling im Sekundentakt. Angestoßen wird bei:
 * Anwendungsstart, `online`-Ereignis des Browsers, Öffnen einer offline
 * gespeicherten Inbetriebnahme, nach einer Änderung (wenn eine Verbindung
 * plausibel ist) und über den Knopf „Jetzt synchronisieren".
 */
@Injectable({ providedIn: 'root' })
export class OfflineSyncService {

  /**
   * Höchstens so viele Änderungen in einem Anlauf. Muss zu
   * `MaxSyncAenderungen` im Controller passen und hält eine einzelne Anfrage
   * kurz genug, um eine dünne Leitung zu überleben.
   */
  private static readonly STAPELGROESSE = 100;

  /** Zeitgrenze für einen Stapel. Danach gilt die Leitung als tot. */
  private static readonly TIMEOUT_MS = 30_000;

  private readonly status$ = new BehaviorSubject<SyncStatus>({
    zustand: 'online',
    ausstehend: 0,
    konflikte: 0,
    fehlgeschlagen: 0,
    meldung: 'Alles synchronisiert'
  });

  /** Verhindert, dass zwei Abgleiche gleichzeitig dieselbe Warteschlange abarbeiten. */
  private laeuft = false;

  /**
   * Ein Abgleich, der während eines laufenden angefordert wurde. Wird danach
   * einmal nachgeholt – so geht ein Anstoß nicht verloren, ohne dass sich
   * Aufrufe stapeln.
   */
  private nachholen = false;

  constructor(
    private api: InbetriebnahmeService,
    private db: OfflineDbService,
    private network: NetworkStatusService
  ) {
    // Kommt das Netz zurück, sofort versuchen: das ist der Moment, in dem der
    // Monteur die Halle verlässt und wieder Empfang hat.
    this.network.online$.subscribe(online => {
      if (online) {
        void this.synchronisiere('online');
      } else {
        void this.aktualisiereStatus();
      }
    });

    /*
     * Zusätzlich beim Zurückkehren zur Anwendung.
     *
     * Auf einem Tablet ist das der häufigste Fall überhaupt: der Bildschirm
     * war aus, das Gerät fährt aus dem Funkloch, und das `online`-Ereignis
     * des Browsers kommt entweder gar nicht oder während die Seite im
     * Hintergrund schlief. Ohne diesen Auslöser bliebe die Warteschlange
     * stehen, bis jemand von Hand auf den Statuschip tippt – und genau so
     * fühlt es sich dann an, als synchronisiere die Anwendung nicht von
     * selbst.
     *
     * Kein Polling: das Ereignis kommt nur, wenn der Benutzer die Anwendung
     * tatsächlich wieder in den Vordergrund holt.
     */
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          void this.synchronisiere('sichtbar');
        }
      });
    }

    void this.aktualisiereStatus();
  }

  get syncStatus$(): Observable<SyncStatus> {
    return this.status$.asObservable();
  }

  get aktuellerStatus(): SyncStatus {
    return this.status$.value;
  }

  // ===================================================================
  // WARTESCHLANGE FÜLLEN
  // ===================================================================

  /**
   * Legt eine Änderung in die Warteschlange.
   *
   * Wird immer ZUSAMMEN mit der lokalen Datenänderung aufgerufen, innerhalb
   * derselben Dexie-Transaktion – siehe `InbetriebnahmeDataService`. Getrennt
   * gespeichert könnte die Oberfläche eine Prüfung zeigen, die nie zum Server
   * geht.
   */
  async stelleEin(eintrag: SyncQueueEintrag): Promise<void> {
    await this.db.db.syncQueue.put(eintrag);
    await this.aktualisiereStatus();
  }

  // ===================================================================
  // ABGLEICH
  // ===================================================================

  /**
   * Schickt die Warteschlange zum Server.
   *
   * Tut nichts, wenn nichts aussteht oder ein Versuch gerade sinnlos wäre
   * (Browser offline, oder kurz zuvor ist eine Anfrage im Netzwerkfehler
   * geendet und die Wartezeit läuft noch).
   */
  async synchronisiere(anlass: SyncAnlass = 'manuell'): Promise<SyncStatus> {
    if (this.laeuft) {
      this.nachholen = true;
      return this.status$.value;
    }

    if (!(await this.db.istVerfuegbar())) {
      return this.status$.value;
    }

    const offen = await this.offeneEintraege();

    if (offen.length === 0) {
      return this.aktualisiereStatus();
    }

    /*
     * Die Wartezeit nach einem Netzwerkfehler gilt für Hintergrundversuche.
     * Übergangen wird sie, wenn ein starkes Signal vorliegt, dass sich die
     * Lage geändert hat: der Benutzer drückt den Knopf, der Browser meldet
     * wieder Netz, oder die Anwendung kommt in den Vordergrund. In allen
     * drei Fällen wäre Abwarten nur störrisch.
     */
    const sofortVersuchen =
      anlass === 'manuell'
      || anlass === 'online'
      || anlass === 'sichtbar';

    if (!sofortVersuchen && !this.network.lohntVersuch()) {
      return this.aktualisiereStatus();
    }

    this.laeuft = true;
    this.setzeStatus({ zustand: 'laeuft', meldung: 'Synchronisierung läuft …' });

    try {
      for (let i = 0; i < offen.length; i += OfflineSyncService.STAPELGROESSE) {
        const stapel = offen.slice(i, i + OfflineSyncService.STAPELGROESSE);

        const abgebrochen = await this.sendeStapel(stapel);

        if (abgebrochen) {
          // Netzwerkfehler: der Rest bleibt liegen und wird beim nächsten
          // Anlauf erneut versucht. Nichts geht verloren.
          break;
        }
      }
    } finally {
      this.laeuft = false;
    }

    const status = await this.aktualisiereStatus();

    if (this.nachholen) {
      this.nachholen = false;
      return this.synchronisiere(anlass);
    }

    return status;
  }

  /**
   * Schickt einen Stapel und wertet die Antwort aus.
   * Gibt `true` zurück, wenn wegen eines Netzwerkfehlers abgebrochen wurde.
   */
  private async sendeStapel(stapel: SyncQueueEintrag[]): Promise<boolean> {
    const changes: SyncAenderung[] = stapel.map(e => ({
      clientId: e.id,
      typ: e.typ,

      // Eine lokal vergebene Kennung ist keine Server-Id – sie geht als
      // clientEntityId mit, damit der Server den inzwischen angelegten
      // Datensatz darüber wiederfindet.
      entityId: typeof e.entityId === 'number' ? e.entityId : null,
      clientEntityId: e.clientEntityId ?? null,

      messstellenListeId: e.messstellenListeId,
      messstelleId: e.messstelleId ?? null,

      // Hinweis für Punkte, die offline aus einer Kategorie entstanden sind.
      vorlageId: e.vorlageId ?? null,

      performedAtUtc: e.performedAtUtc,
      baseVersion: e.baseVersion ?? null,
      payload: e.payload
    }));

    let ergebnis: SyncErgebnis;

    try {
      ergebnis = await firstValueFrom(
        this.api.sync(changes).pipe(
          timeout({
            each: OfflineSyncService.TIMEOUT_MS,
            // Muss ein Observable liefern - ein synchrones throw im Fabrikaufruf
            // waere ein anderer Fehlerweg und ginge an istNetzwerkfehler vorbei.
            with: () => throwError(() => new TimeoutFehler(OfflineSyncService.TIMEOUT_MS))
          })
        )
      );

      this.network.meldeErfolg();
    } catch (fehler: any) {
      if (NetworkStatusService.istAuthFehler(fehler)) {
        /*
         * Die Anmeldung ist abgelaufen. Die Warteschlange bleibt UNANGETASTET
         * – nach dem nächsten Login geht es weiter. Ein Datenverlust wegen
         * eines abgelaufenen Tokens wäre der schlimmste denkbare Fehler
         * dieser Funktion.
         */
        this.setzeStatus({
          zustand: 'fehler',
          meldung: 'Anmeldung abgelaufen – nach dem nächsten Login wird weiter synchronisiert.'
        });

        return true;
      }

      if (NetworkStatusService.istNetzwerkfehler(fehler)) {
        this.network.meldeNetzwerkfehler();

        // Nur den Zähler hochsetzen. Der Eintrag selbst bleibt unverändert –
        // insbesondere seine Kennung, die die Idempotenz trägt.
        await this.db.db.transaction('rw', this.db.db.syncQueue, async () => {
          for (const e of stapel) {
            await this.db.db.syncQueue.update(e.id, {
              retryCount: (e.retryCount ?? 0) + 1,
              letzterFehler: 'Keine Verbindung zum Server.'
            });
          }
        });

        return true;
      }

      // Alles andere (400 auf Stapelebene, 500) ist eine echte Antwort. Der
      // Stapel bleibt liegen, damit nichts stillschweigend verschwindet.
      await this.db.db.transaction('rw', this.db.db.syncQueue, async () => {
        for (const e of stapel) {
          await this.db.db.syncQueue.update(e.id, {
            retryCount: (e.retryCount ?? 0) + 1,
            letzterFehler: fehler?.error?.message ?? 'Der Server hat die Übertragung abgelehnt.'
          });
        }
      });

      return true;
    }

    await this.verarbeiteAntwort(stapel, ergebnis);

    return false;
  }

  /**
   * Wertet die Antwort aus: Erfolge räumen die Warteschlange, Konflikte
   * wandern in die Konfliktliste, endgültige Fehler bleiben sichtbar liegen.
   */
  private async verarbeiteAntwort(
    stapel: SyncQueueEintrag[],
    ergebnis: SyncErgebnis
  ): Promise<void> {
    const db = this.db.db;
    const nachId = new Map(stapel.map(e => [e.id, e]));
    const listenIds = new Set(stapel.map(e => e.messstellenListeId));

    await db.transaction(
      'rw',
      db.syncQueue,
      db.konflikte,
      db.details,
      db.messstellen,
      db.listen,
      async () => {
        // ── Erfolge ──
        for (const erfolg of ergebnis.successful ?? []) {
          const eintrag = nachId.get(erfolg.clientId);

          await db.syncQueue.delete(erfolg.clientId);

          if (!eintrag) {
            continue;
          }

          /*
           * Ein offline angelegter Prüfpunkt hat jetzt eine echte Id. Die
           * lokale Kennung muss überall nachgezogen werden – im gespeicherten
           * Detaildatensatz und in allen noch wartenden Änderungen, die sich
           * auf ihn beziehen.
           */
          if (erfolg.serverId != null && eintrag.clientEntityId) {
            await this.ersetzeLokaleKennung(
              eintrag.messstelleId ?? null,
              eintrag.clientEntityId,
              erfolg.serverId
            );
          }

          if (eintrag.messstelleId != null) {
            await this.uebernehmeVersion(
              eintrag,
              erfolg.serverId ?? erfolg.entityId ?? null,
              erfolg.version ?? null
            );
          }
        }

        // ── Konflikte ──
        for (const konflikt of ergebnis.conflicts ?? []) {
          const eintrag = nachId.get(konflikt.clientId);

          if (!eintrag) {
            continue;
          }

          /*
           * Aus der Warteschlange heraus in die Konfliktliste. Der
           * ursprüngliche Eintrag wandert mit – wenn der Benutzer sich für
           * seine Fassung entscheidet, wird genau er erneut eingestellt.
           *
           * Wichtig: die Kennung bleibt dieselbe. Der Server hat die Änderung
           * ja NICHT angewendet, also ist sie noch nicht im Journal – ein
           * erneutes Senden unter derselben Kennung ist korrekt.
           */
          const konfliktEintrag: SyncKonfliktEintrag = {
            id: konflikt.clientId,
            typ: konflikt.typ,
            messstellenListeId: eintrag.messstellenListeId,
            messstelleId: eintrag.messstelleId ?? null,
            entityId: konflikt.entityId ?? null,
            bezeichnung: konflikt.bezeichnung,
            feld: konflikt.feld,
            serverText: konflikt.serverText,
            lokalText: konflikt.lokalText,
            serverVersion: konflikt.serverVersion,
            serverBenutzer: konflikt.serverBenutzer ?? null,
            serverZeitpunktUtc: konflikt.serverZeitpunktUtc ?? null,
            erkanntAmUtc: new Date().toISOString(),
            aenderung: eintrag
          };

          await db.konflikte.put(konfliktEintrag);
          await db.syncQueue.delete(konflikt.clientId);
        }

        // ── Fehler ──
        for (const fehler of ergebnis.failed ?? []) {
          if (fehler.wiederholbar) {
            await db.syncQueue.update(fehler.clientId, {
              retryCount: (nachId.get(fehler.clientId)?.retryCount ?? 0) + 1,
              letzterFehler: fehler.message
            });
            continue;
          }

          /*
           * Endgültig abgelehnt – erneutes Senden ändert nichts. Der Eintrag
           * bleibt trotzdem stehen, markiert und sichtbar: der Benutzer soll
           * erfahren, was nicht angekommen ist, statt es stillschweigend zu
           * verlieren.
           */
          console.error(
            'Der Server hat eine Offline-Änderung endgültig abgelehnt.',
            {
              typ: fehler.typ,
              statusCode: fehler.statusCode,
              meldung: fehler.message,
              entityId: fehler.entityId,
              aenderung: nachId.get(fehler.clientId)
            }
          );

          await db.syncQueue.update(fehler.clientId, {
            endgueltigFehlgeschlagen: true,
            letzterFehler: `${fehler.message} (${fehler.statusCode})`
          });
        }

        // ── Zeitstempel der Listen ──
        const jetzt = new Date().toISOString();

        for (const listeId of listenIds) {
          const liste = await db.listen.get(listeId);

          if (liste) {
            await db.listen.update(listeId, { lastSyncAtUtc: jetzt });
          }
        }
      }
    );
  }

  /**
   * Ersetzt die lokale Kennung eines offline angelegten Prüfpunkts durch die
   * vom Server vergebene Id – im gespeicherten Detaildatensatz und in allen
   * noch wartenden Änderungen.
   */
  private async ersetzeLokaleKennung(
    messstelleId: number | null,
    clientEntityId: string,
    serverId: number
  ): Promise<void> {
    const db = this.db.db;

    if (messstelleId != null) {
      const detail = await db.details.get(messstelleId);

      if (detail) {
        for (const punkt of detail.data.pruefpunkte ?? []) {
          if (punkt.clientId === clientEntityId || punkt.id === `local:${clientEntityId}`) {
            punkt.id = serverId;
            punkt.clientId = clientEntityId;
            punkt.nichtSynchronisiert = false;
          }
        }

        await db.details.put(detail);
      }
    }

    // Wartende Änderungen, die sich auf den eben angelegten Punkt beziehen,
    // bekommen jetzt die echte Id. Ohne das müsste der Server sie weiterhin
    // über die Kennung auflösen – er kann das, aber die lokale Anzeige wäre
    // uneinheitlich.
    const betroffen = await db.syncQueue
      .filter(e => e.clientEntityId === clientEntityId)
      .toArray();

    for (const e of betroffen) {
      await db.syncQueue.update(e.id, { entityId: serverId });
    }
  }

  /**
   * Übernimmt den neuen Änderungsstand vom Server in den lokalen Bestand.
   *
   * Ohne das würde die nächste Offline-Änderung mit einer veralteten Basis
   * losgeschickt – und der Server meldete zu Recht einen Konflikt, obwohl der
   * Benutzer der einzige war, der geschrieben hat.
   */
  private async uebernehmeVersion(
    eintrag: SyncQueueEintrag,
    entityId: number | null,
    version: number | null
  ): Promise<void> {
    if (version == null || eintrag.messstelleId == null) {
      return;
    }

    const db = this.db.db;
    const detail = await db.details.get(eintrag.messstelleId);

    if (!detail) {
      return;
    }

    if (eintrag.typ === 'PRUEFPUNKT_ERGEBNIS') {
      for (const punkt of detail.data.pruefpunkte ?? []) {
        if (punkt.id === entityId || punkt.id === eintrag.entityId) {
          punkt.version = version;
          punkt.nichtSynchronisiert = false;
        }
      }
    } else {
      detail.data.version = version;
    }

    await db.details.put(detail);
  }

  // ===================================================================
  // KONFLIKTE AUFLÖSEN
  // ===================================================================

  /**
   * Die Serverfassung gilt.
   *
   * Die eigene Änderung wird verworfen und der betroffene Datensatz aus dem
   * lokalen Bestand entfernt, damit er beim nächsten Öffnen frisch vom Server
   * kommt. Nichts wird still überschrieben – der Benutzer hat entschieden.
   */
  async uebernehmeServerfassung(konfliktId: string): Promise<void> {
    const db = this.db.db;
    const konflikt = await db.konflikte.get(konfliktId);

    if (!konflikt) {
      return;
    }

    await db.transaction('rw', db.konflikte, db.details, async () => {
      if (konflikt.messstelleId != null) {
        // Der lokale Detaildatensatz ist jetzt nachweislich veraltet. Löschen
        // statt raten: die Detailseite lädt ihn beim nächsten Öffnen neu,
        // sofern eine Verbindung besteht.
        await db.details.delete(konflikt.messstelleId);
      }

      await db.konflikte.delete(konfliktId);
    });

    await this.aktualisiereStatus();
  }

  /**
   * Die eigene, offline erfasste Fassung gilt.
   *
   * Die Änderung geht erneut in die Warteschlange – diesmal mit dem
   * Änderungsstand des Servers als Basis. Damit lässt der Server sie durch,
   * und zwar bewusst: der Benutzer hat die Serverfassung gesehen und sich
   * dagegen entschieden.
   */
  async uebernehmeLokaleFassung(konfliktId: string): Promise<void> {
    const db = this.db.db;
    const konflikt = await db.konflikte.get(konfliktId);

    if (!konflikt) {
      return;
    }

    const erneut: SyncQueueEintrag = {
      ...konflikt.aenderung,

      // Der Server kennt die Kennung noch nicht – er hat die Änderung ja
      // abgelehnt. Sie bleibt deshalb dieselbe und trägt die Idempotenz
      // weiterhin.
      baseVersion: konflikt.serverVersion,
      retryCount: 0,
      letzterFehler: null,
      endgueltigFehlgeschlagen: false
    };

    await db.transaction('rw', db.konflikte, db.syncQueue, async () => {
      await db.syncQueue.put(erneut);
      await db.konflikte.delete(konfliktId);
    });

    await this.aktualisiereStatus();
    await this.synchronisiere('manuell');
  }

  /** Alle offenen Konflikte, für die Anzeige. */
  async ladeKonflikte(listeId?: number): Promise<SyncKonfliktEintrag[]> {
    return this.db.ladeKonflikte(listeId);
  }

  /**
   * Änderungen, die der Server endgültig abgelehnt hat.
   *
   * Sie bleiben sichtbar liegen, statt still zu verschwinden: der Benutzer
   * soll erfahren, was NICHT angekommen ist – und es gegebenenfalls von Hand
   * nachtragen.
   */
  async ladeFehlgeschlagene(listeId?: number): Promise<SyncQueueEintrag[]> {
    const alle = await this.db.ladeWarteschlange(listeId);

    return alle.filter(e => e.endgueltigFehlgeschlagen === true);
  }

  /**
   * Verwirft endgültig abgelehnte Änderungen. Ausdrücklich vom Benutzer
   * angestoßen – automatisch verschwindet hier nichts.
   */
  async verwerfeFehlgeschlagene(): Promise<number> {
    const db = this.db.db;

    const betroffen = await db.syncQueue
      .filter(e => e.endgueltigFehlgeschlagen === true)
      .toArray();

    await db.syncQueue.bulkDelete(betroffen.map(e => e.id));
    await this.aktualisiereStatus();

    return betroffen.length;
  }

  // ===================================================================
  // STATUS
  // ===================================================================

  /** Änderungen, die noch einen Versuch wert sind. */
  private async offeneEintraege(): Promise<SyncQueueEintrag[]> {
    const alle = await this.db.ladeWarteschlange();

    return alle.filter(e => !e.endgueltigFehlgeschlagen);
  }

  async aktualisiereStatus(): Promise<SyncStatus> {
    if (!(await this.db.istVerfuegbar())) {
      return this.status$.value;
    }

    const alle = await this.db.ladeWarteschlange();
    const konflikte = await this.db.zaehleKonflikte();

    const abgelehnt = alle.filter(e => e.endgueltigFehlgeschlagen);
    const fehlgeschlagen = abgelehnt.length;
    const ausstehend = alle.length - fehlgeschlagen;

    const letzterSync = await this.letzterSyncStand();

    const status: SyncStatus = {
      zustand: this.ermittleZustand(ausstehend, konflikte, fehlgeschlagen),
      ausstehend,
      konflikte,
      fehlgeschlagen,
      letzterSyncUtc: letzterSync,
      letzterFehlertext: abgelehnt[0]?.letzterFehler ?? null,
      meldung: ''
    };

    status.meldung = this.meldungZu(status);

    this.status$.next(status);

    return status;
  }

  private ermittleZustand(
    ausstehend: number,
    konflikte: number,
    fehlgeschlagen: number
  ): SyncStatus['zustand'] {
    if (this.laeuft) {
      return 'laeuft';
    }

    // Konflikte zuerst: sie brauchen eine Entscheidung und sind damit
    // dringender als alles andere.
    if (konflikte > 0) {
      return 'konflikt';
    }

    if (fehlgeschlagen > 0) {
      return 'fehler';
    }

    if (!this.network.browserMeldetOnline) {
      return 'offline';
    }

    if (ausstehend > 0) {
      return 'ausstehend';
    }

    return 'online';
  }

  private meldungZu(status: SyncStatus): string {
    switch (status.zustand) {
      case 'laeuft':
        return 'Synchronisierung läuft …';

      case 'konflikt':
        return status.konflikte === 1
          ? '1 Konflikt braucht eine Entscheidung'
          : `${status.konflikte} Konflikte brauchen eine Entscheidung`;

      case 'fehler':
        return status.fehlgeschlagen === 1
          ? '1 Änderung konnte nicht synchronisiert werden'
          : `${status.fehlgeschlagen} Änderungen konnten nicht synchronisiert werden`;

      case 'offline':
        return status.ausstehend > 0
          ? `Offline · ${status.ausstehend} ${status.ausstehend === 1 ? 'Änderung' : 'Änderungen'} ausstehend`
          : 'Offline';

      case 'ausstehend':
        return `${status.ausstehend} ${status.ausstehend === 1 ? 'Änderung' : 'Änderungen'} ausstehend`;

      default:
        return 'Online · Alles synchronisiert';
    }
  }

  private async letzterSyncStand(): Promise<string | null> {
    const listen = await this.db.ladeListen();

    const staende = listen
      .map(l => l.lastSyncAtUtc)
      .filter((s): s is string => !!s)
      .sort();

    return staende.length > 0 ? staende[staende.length - 1] : null;
  }

  private setzeStatus(teil: Partial<SyncStatus>): void {
    this.status$.next({ ...this.status$.value, ...teil });
  }

  /**
   * Hilfsmittel für die Aufrufer: gehört diese Kennung zu einem noch nicht
   * synchronisierten Datensatz?
   */
  static istLokal(id: unknown): boolean {
    return istLokaleId(id as any);
  }
}
